import EmptyState from "@/components/EmptyState";
import Table, { buildDocumentLinkColumn } from "@/components/Table";
import { useQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import { GET_JOB_APPLICATIONS_QUERY } from "./queries";
import { toDateFormat } from "@/lib/dateHelpers";
import MetricGrid from "@/components/MetricGrid";
import { getStickyColumnClasses } from "@/lib/styleHelpers";

import { useRequireCapability, useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import { PERMISSION } from "@/hooks/useCapabilities";
import { DOMAIN } from "@/hooks/useCapabilities";
import IconButton from "@/components/IconButton";
import { SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { CellContext } from "@tanstack/react-table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { Review } from "@/pages/_layouts/jobs/types";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import UpdateJobApplicationFilesModal from "@/components/UpdateJobApplicationFilesModal";

// Local type for job application row
interface JobApplicationRow {
  id: string;
  applicationId: string;
  jobTitle: string;
  facility: string;
  serviceLevel: string | null;
  email: string;
  phone: string;
  resume: string | null;
  coverLetter: string | null;
  website: string | null;
  appliedOn: string;
  reviewScoreAverage: number | null;
  reviews: Array<{
    id: string;
    createdAt: string;
    updatedAt: string;
    comments: string;
    score: number;
    isRecommendation: boolean;
    reviewedBy: {
      id: string;
      name: string;
      email: string;
    };
  }>;
}

type PersonData = {
  person: {
    jobApplications: any[];
    totalJobApplications: number;
    totalActiveJobApplications: number;
  };
};

const formatTableData = (list: any[]): JobApplicationRow[] =>
  list.map((app) => ({
    id: app.jobPost?.id,
    applicationId: app.id,
    jobTitle: app.jobPost?.name || "--",
    facility: app.jobPost?.facility?.name || "--",
    serviceLevel: app.jobPost?.serviceLevel || null,
    email: app.email || null,
    phone: app.phone || null,
    resume: app.resume || null,
    coverLetter: app.coverLetter || null,
    website: app.website || null,
    appliedOn: app.appliedOn || null,
    reviewScoreAverage: app.reviewScoreAverage || null,
    reviews: app.reviews?.filter((r: Review) => !r.isRecommendation) || []
  }));

export default function JobApplications() {
  useRequireCapability({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });
  const canManageJobPosts = useCanManageJobPostsBool();
  const { pgaId } = useParams();
  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();

  const [sorting, setSorting] = useState<SortingState>([{ id: "appliedOn", desc: true }]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);

  const { data, loading, error } = useQuery<PersonData>(GET_JOB_APPLICATIONS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first"
  });

  const tableData = data?.person?.jobApplications ? formatTableData(data.person.jobApplications) : [];
  const total = data?.person?.totalJobApplications ? String(data.person.totalJobApplications) : "0";
  const active = data?.person?.totalActiveJobApplications ? String(data.person.totalActiveJobApplications) : "0";

  const totalConsultantReviews = tableData.reduce((sum, app) => sum + (app.reviews?.length || 0), 0);

  const appsWithScores = tableData.filter((app) => typeof app.reviewScoreAverage === "number");
  const averageReviewRating = appsWithScores.length
    ? (appsWithScores.reduce((sum, app) => sum + app.reviewScoreAverage!, 0) / appsWithScores.length).toFixed(1)
    : "0";

  if (error) return <div>Error: {error.message}</div>;

  const metrics: MetricCardType<Path>[] = [
    {
      label: "Total Job Applications",
      value: total,
      iconName: "business_center"
    },
    {
      label: "Active Job Applications",
      value: active,
      iconName: "business_center"
    },
    {
      label: "Total Consultant Reviews",
      value: totalConsultantReviews.toString(),
      iconName: "rate_review"
    },
    {
      label: "Average Review Rating",
      value: averageReviewRating,
      iconName: "rate_review"
    }
  ];

  return (
    <>
      <MetricGrid metrics={metrics} layout="expanded" />
      <div className="pt-12" />
      <Table
        loading={loading}
        data={tableData}
        sortingState={sorting}
        onSortingChange={setSorting}
        columns={[
          {
            header: "Job Title",
            id: "jobTitle",
            accessorKey: "jobTitle",
            size: 200,
            enableSorting: true,
            meta: { className: getStickyColumnClasses(!!tableData?.length) }
          },
          {
            header: "Facility",
            id: "facility",
            accessorKey: "facility",
            enableSorting: true
          },
          buildDocumentLinkColumn<JobApplicationRow>({
            accessorKey: "resume",
            header: "Resume",
            getDocumentUrl: (row) => row.resume,
            getDocumentName: (row) => (row.resume ? "Resume" : null)
          }),
          buildDocumentLinkColumn<JobApplicationRow>({
            accessorKey: "coverLetter",
            header: "Cover Letter",
            getDocumentUrl: (row) => row.coverLetter,
            getDocumentName: (row) => (row.coverLetter ? "Cover Letter" : null)
          }),
          buildDocumentLinkColumn<JobApplicationRow>({
            accessorKey: "website",
            header: "Website",
            getDocumentUrl: (row) => row.website,
            getDocumentName: (row) => (row.website ? "Website" : null)
          }),
          {
            id: "appliedOn",
            header: "Applied On",
            accessorKey: "appliedOn",
            enableSorting: true,
            cell: ({ row }) => toDateFormat(row.original.appliedOn, { tz: "utc" })
          },
          {
            id: "reviews",
            header: "Reviews / Avg. Rating",
            accessorKey: "reviews",
            cell: ({ row }) => {
              const expanded = row.getIsExpanded();
              const reviews = row.original.reviews || [];
              const avg = row.original.reviewScoreAverage;
              return reviews.length > 0 ? (
                <div className="flex items-center py-2">
                  <span>
                    {reviews.length} / {avg}
                  </span>
                  <IconButton
                    name={expanded ? "expand_less" : "expand_more"}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      row.toggleExpanded();
                    }}
                    className={`ml-2 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-label={expanded ? "Collapse reviews" : "Expand reviews"}
                  />
                </div>
              ) : (
                <div className="flex items-center py-2 text-body-medium opacity-20">--</div>
              );
            }
          },
          {
            cell({ row }) {
              const actions: Action[] = [
                {
                  text: "View Job Application",
                  onClick: () => {
                    if (row.original.id) {
                      navigate(`/jobs/job-post/${row.original.id}/applicants`);
                    }
                  }
                },
                ...(canManageJobPosts
                  ? [
                      {
                        text: "Update Files",
                        onClick: () => {
                          setSelectedApplicationId(row.original.applicationId);
                          setUpdateModalOpen(true);
                        }
                      } satisfies Action
                    ]
                  : [])
              ];

              function onClick() {
                setMenu({ rowId: row.id, actions });
              }

              return (
                <div
                  className="flex flex-row justify-end"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconButton
                    id={actionId(row.id)}
                    onClick={onClick}
                    data-testid="actions"
                    title="actions"
                    name="more_vert"
                    iconClassName="font-extrabold"
                    className="!m-0 h-10"
                  />
                </div>
              );
            },
            id: "actions",
            size: 100
          }
        ]}
        renderExpandedRow={({ row }) => {
          return (
            <div className="bg-sys-surface-container-lowest rounded-b-xl p-2">
              <TableDetailsPanel
                data={row.original.reviews || []}
                columns={[
                  {
                    accessorKey: "reviewedBy.name",
                    header: "Reviewer",
                    size: 520,
                    cell: (cellProps: CellContext<any, any>) => cellProps.row.original.reviewedBy?.name || "--"
                  },
                  {
                    accessorKey: "createdAt",
                    header: "Review Date",
                    size: 140,
                    cell: (cellProps: CellContext<any, any>) =>
                      cellProps.row.original.createdAt
                        ? new Date(cellProps.row.original.createdAt).toLocaleDateString(undefined, {
                            month: "short",
                            day: "2-digit",
                            year: "numeric"
                          })
                        : "--"
                  },
                  {
                    accessorKey: "score",
                    header: "Rating",
                    size: 140
                  },
                  {
                    accessorKey: "comments",
                    header: "Review Comments",
                    size: 520
                  }
                ]}
              />
            </div>
          );
        }}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Job Applications"
            caption="This person has not submitted any job applications."
            iconName="work"
          />
        )}
        data-testid="job-applications-table"
        getRowLink={(row) => (row.original.id ? { to: `/jobs/job-post/${row.original.id}/applicants` as Path } : null)}
      />
      <Menu />
      {updateModalOpen && selectedApplicationId && (
        <UpdateJobApplicationFilesModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
          applicationId={selectedApplicationId}
          onSuccess={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
        />
      )}
    </>
  );
}
